import { Box, Flex, TextV2 } from '@withjoy/joykit';
import React from 'react';
import { CONTAINER_BLOCK_PADDING_X, CONTENT_BLOCK_MAX_WIDTH, FULL_BLEED_MARGIN_X, getBrannanMobileMediaQuery } from '../../LayoutAloha.theme';
import { StyledTitle } from './Page.styles';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { PrettyLink } from '@apps/guest/packages/layout-engine/components/PrettyLink';
import { ResponsiveText } from '@apps/guest/packages/layout-engine/components/ResponsiveText';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { body4ToSubHeadingOverride, display3ToPageTitleOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import type { PhotoV2WithoutBlurHashFragment } from '@graphql/fragments/photo.graphql.generated';
import type { EventPageVisibility, EventPageType } from '@graphql/generated-base';
import { Category, DesignLayoutType } from '@graphql/generated-base';
import CoverPhoto from '../../../../components/CoverPhoto';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { InlineEditor } from '@apps/guest/shared/components/InlineEditor';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useTranslation } from '@shared/core';
import { isInIframe } from '@shared/utils/isInIframe';
import { AccommodationsCountdownExperiment } from '@apps/guest/components/AccommodationsCountdownExperiment';
import { useColorPaletteProvider } from '../../../ColorPaletteProvider/ColorPaletteProvider';
interface Props
  extends Readonly<{
    title?: Maybe<string>;
    subtitle?: Maybe<string>;
    graphicAccent?: Maybe<JSX.Element>;
    marginTop?: string | string[];
    isAccommodations?: boolean;
    pageSlug?: string;
    photo?: Maybe<PhotoV2WithoutBlurHashFragment>;
    pageId: string;
    pageType: EventPageType;
    visibility: EventPageVisibility;
    isPageContainer?: boolean;
  }> {}

export const Page = React.forwardRef<HTMLDivElement, React.PropsWithChildren<Props>>((props, pageRef) => {
  const { title, subtitle, graphicAccent, children, isAccommodations, pageSlug, photo, pageId, pageType, visibility, isPageContainer, ...restProps } = props;

  const isSmallScreen = useMediaQuery(getBrannanMobileMediaQuery);
  const isPreviewing = isInIframe();
  const { pageFontColor } = useColorPaletteProvider();

  const { tq } = useTranslation('guestKit');

  const onInlineEditClick = useEventCallback(() => {
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: {
        action: 'editPageNote',
        inlineEditData: { pageId, isPageContainer: isPageContainer || false, pageSubTitle: subtitle || '' },
      },
    });
  });

  return isAccommodations ? (
    <Flex
      ref={pageRef}
      {...restProps}
      paddingX={CONTAINER_BLOCK_PADDING_X}
      width="100%"
      justifyContent={'center'}
      flexDirection={'column'}
    >
      {isSmallScreen && photo && (
        <Box
          paddingBottom={{ _: 9, sm2: 10 }}
          marginX={FULL_BLEED_MARGIN_X}
        >
          <CoverPhoto
            websiteLayoutType={DesignLayoutType.aloha}
            photo={photo}
            pageId={pageId}
            pageType={pageType}
            pageSlug={pageSlug}
          />
        </Box>
      )}
      {graphicAccent}
      {title && (
        <GuestSiteTypographyOverride override={display3ToPageTitleOverride}>
          <ResponsiveText
            typographyVariant="display3"
            defaultFontSize={36}
            marginTop={{ _: 0, sm2: 5 }}
            marginX={6}
            textAlign={'center'}
            wordBreak="break-word"
            text={title}
            skipResize={false}
            fontCategory={Category.PAGE_TITLE}
          />
        </GuestSiteTypographyOverride>
      )}
      <GuestSiteTypographyOverride override={body4ToSubHeadingOverride}>
        <TextV2
          as="div"
          typographyVariant="body4"
          marginTop={subtitle || isPreviewing ? 7 : 0}
          marginX={'auto'}
          textAlign={'center'}
          maxWidth={CONTENT_BLOCK_MAX_WIDTH}
          wordBreak="break-word"
          color={pageFontColor || 'mono14'}
        >
          <InlineEditor
            actionData={{ edit: onInlineEditClick }}
            wrapperType="actionOutside"
            componentName="pageNote"
            elementLabel={tq`Page Note`}
            pageName={pageType}
            pageSlug={pageSlug || ''}
            stickyOnScroll={true}
            wrapperCSS={{ zIndex: 30 }}
            isEligibleForInlineEditing={true}
          >
            {subtitle ? (
              <PrettyLink
                source={pageSlug}
                allowedTypes={['EMAIL']}
              >
                {subtitle}
              </PrettyLink>
            ) : (
              isPreviewing && (
                <TextV2
                  opacity={0.7}
                  fontStyle="italic"
                >
                  {tq`You can share a note with your guests here!`}
                </TextV2>
              )
            )}
          </InlineEditor>
        </TextV2>
      </GuestSiteTypographyOverride>
      <AccommodationsCountdownExperiment />
      {children}
    </Flex>
  ) : (
    <Box
      ref={pageRef}
      {...restProps}
      paddingX={CONTAINER_BLOCK_PADDING_X}
      marginX={'auto'}
    >
      {isSmallScreen && photo && (
        <Box
          paddingBottom={{ _: 9, sm2: 10 }}
          marginX={FULL_BLEED_MARGIN_X}
        >
          <CoverPhoto
            websiteLayoutType={DesignLayoutType.aloha}
            photo={photo}
            pageId={pageId}
            pageType={pageType}
            pageSlug={pageSlug}
          />
        </Box>
      )}
      {graphicAccent}
      {title ? (
        <GuestSiteTypographyOverride override={display3ToPageTitleOverride}>
          <StyledTitle
            typographyVariant="display3"
            marginTop={{ _: 0, sm2: 5 }}
            textAlign={'center'}
          >
            {title}
          </StyledTitle>
        </GuestSiteTypographyOverride>
      ) : null}
      {subtitle ? (
        <GuestSiteTypographyOverride override={body4ToSubHeadingOverride}>
          <TextV2
            as="div"
            typographyVariant="body4"
            marginTop={7}
            marginX="auto"
            textAlign="center"
            maxWidth={CONTENT_BLOCK_MAX_WIDTH}
            wordBreak="break-word"
          >
            <PrettyLink source={pageSlug}>{subtitle}</PrettyLink>
          </TextV2>
        </GuestSiteTypographyOverride>
      ) : null}
      {children}
    </Box>
  );
});
